import React, { useCallback, useEffect, useRef } from 'react';
// @ts-ignore
import { HiddenWebview } from 'capacitor-hidden-webview';
import { Capacitor } from '@capacitor/core';
import { PluginListenerHandle } from '@capacitor/core';
import styled from 'styled-components';
import { PHONE_MEDIA_QUERY } from '../../common/responsive';
import {
  sendEvent,
  EVENT_APPLE_PAY_PAYMENT_COMPLETED,
  EVENT_APPLE_PAY_PAYMENT_CANCELLED,
  EVENT_APPLE_PAY_PAYMENT_FAILED,
} from '../../common/analytics';

const IframeWrapper = styled.div`
  width: 100%;
  height: 650px;
  margin-top: 12px;
  @media ${PHONE_MEDIA_QUERY} {
    height: 48px;
    margin-bottom: 8px;
    margin-top: 8px;
  }
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 650px;
  border: none;
  border-radius: 8px;
  @media ${PHONE_MEDIA_QUERY} {
    height: 48px;
  }
`;

type MessageCallbacks = {
  setIsProcessingPayment?: (loading: boolean) => void;
  setTransactionStatus?: (
    status: 'pending' | 'success' | 'error' | null,
    data?: Record<string, unknown>,
  ) => void;
  onAlert?: (
    title: string,
    message: string,
    type: 'success' | 'error' | 'info',
  ) => void;
  timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
  onAnalyticsEvent?: (
    eventName: string,
    params: Record<string, unknown>,
  ) => void;
  onCoinbaseApplePayInteraction?: () => void;
  onPaymentCancelled?: () => void;
};

/** Fee object from onramp API */
type OrderFee = { type?: string; amount?: string; currency?: string };

/** Order payload from Coinbase onramp postMessage (commit_success / polling_success) */
export type ApplePayOrderPayload = {
  orderId?: string;
  paymentTotal?: string;
  paymentSubtotal?: string;
  paymentCurrency?: string;
  paymentMethod?: string;
  purchaseAmount?: string;
  purchaseCurrency?: string;
  fees?: OrderFee[];
  exchangeRate?: string;
  destinationAddress?: string;
  destinationNetwork?: string;
  status?: string;
  txHash?: string;
  createdAt?: string;
  updatedAt?: string;
  partnerUserRef?: string;
};

/** Full postMessage payload: eventName + optional error data or order */
type ApplePayPostMessagePayload = {
  eventName: string;
  data?: { errorCode?: string; errorMessage?: string };
  order?: ApplePayOrderPayload;
};

function handlePostMessage(
  data: ApplePayPostMessagePayload,
  callbacks: MessageCallbacks,
) {
  const { eventName } = data;
  const {
    setIsProcessingPayment,
    setTransactionStatus,
    onAlert,
    timeoutRef,
    onAnalyticsEvent,
    onCoinbaseApplePayInteraction,
    onPaymentCancelled,
  } = callbacks;

  const fireInteractionOnce = () => {
    onCoinbaseApplePayInteraction?.();
  };

  const clearLoadTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  switch (eventName) {
    case 'onramp_api.load_success':
      setIsProcessingPayment?.(true);
      break;

    case 'onramp_api.cancel':
      clearLoadTimeout();
      onPaymentCancelled?.();
      onAnalyticsEvent?.(EVENT_APPLE_PAY_PAYMENT_CANCELLED, {});
      fireInteractionOnce();
      onAlert?.(
        'Payment Cancelled',
        'The payment was cancelled by the user',
        'info',
      );
      setIsProcessingPayment?.(false);
      break;

    case 'onramp_api.commit_error':
      clearLoadTimeout();
      fireInteractionOnce();
      if (data.data?.errorCode === 'ERROR_CODE_GUEST_APPLE_PAY_NOT_SUPPORTED') {
        break;
      }
      onAnalyticsEvent?.(EVENT_APPLE_PAY_PAYMENT_FAILED, {
        reason: 'commit_error',
        error_code: data.data?.errorCode,
        error_message: data.data?.errorMessage,
      });
      onAlert?.('Payment Error', data.data?.errorMessage ?? '', 'error');
      setIsProcessingPayment?.(false);
      break;

    case 'onramp_api.load_error':
      clearLoadTimeout();
      fireInteractionOnce();
      if (data.data?.errorCode === 'ERROR_CODE_GUEST_APPLE_PAY_NOT_SUPPORTED') {
        break;
      }
      onAnalyticsEvent?.(EVENT_APPLE_PAY_PAYMENT_FAILED, {
        reason: 'load_error',
        error_code: data.data?.errorCode,
        error_message: data.data?.errorMessage,
      });
      onAlert?.('Payment Error', data.data?.errorMessage ?? '', 'error');
      setIsProcessingPayment?.(false);
      break;

    case 'onramp_api.commit_success':
      clearLoadTimeout();
      onAnalyticsEvent?.(EVENT_APPLE_PAY_PAYMENT_COMPLETED, {
        stage: 'commit_success',
      });
      fireInteractionOnce();
      onAlert?.(
        'Payment Successful!',
        'Your payment has been processed. Delivering crypto…',
        'success',
      );
      setTransactionStatus?.('pending', data);
      setIsProcessingPayment?.(false);
      break;

    case 'onramp_api.polling_start':
      timeoutRef.current = setTimeout(() => {
        onAlert?.(
          'Delivery Taking Longer',
          'Crypto delivery is taking longer than expected.',
          'info',
        );
      }, 300000);
      break;

    case 'onramp_api.polling_success':
      clearLoadTimeout();
      onAnalyticsEvent?.(EVENT_APPLE_PAY_PAYMENT_COMPLETED, {
        stage: 'polling_success',
      });
      setTransactionStatus?.('success', data);
      onAlert?.('Complete!', 'Your crypto has been delivered!', 'success');
      setIsProcessingPayment?.(false);
      break;

    case 'onramp_api.polling_error':
      clearLoadTimeout();
      onAnalyticsEvent?.(EVENT_APPLE_PAY_PAYMENT_FAILED, {
        reason: 'polling_error',
        error_message: data.data?.errorMessage,
      });
      setTransactionStatus?.('error', data);
      onAlert?.(
        'Transaction Failed',
        data.data?.errorMessage ?? 'Blockchain error',
        'error',
      );
      break;
  }
}

export function ApplePayWidget({
  paymentUrl,
  setIsProcessingPayment,
  setTransactionStatus,
  onAlert,
  forceFeature = false,
  analyticsSource = 'unknown',
  onCoinbaseApplePayInteraction,
  onPaymentCancelled,
  onLoad,
}: {
  paymentUrl: string;
  setIsProcessingPayment?: (loading: boolean) => void;
  setTransactionStatus?: (
    status: 'pending' | 'success' | 'error' | null,
    data?: Record<string, unknown>,
  ) => void;
  onAlert?: (
    title: string,
    message: string,
    type: 'success' | 'error' | 'info',
  ) => void;
  forceFeature: boolean;
  analyticsSource?: string;
  onCoinbaseApplePayInteraction?: () => void;
  onPaymentCancelled?: () => void;
  onLoad?: () => void;
}) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasFiredInteractionRef = useRef(false);
  const isWeb = !Capacitor.isNativePlatform();

  const fireInteractionOnce = useCallback(() => {
    if (hasFiredInteractionRef.current) return;
    hasFiredInteractionRef.current = true;
    onCoinbaseApplePayInteraction?.();
  }, [onCoinbaseApplePayInteraction]);

  const baseParams = forceFeature ? '&forceFeature=true' : '';
  const sandboxParam =
    isWeb &&
    typeof window !== 'undefined' &&
    window.location?.hostname === 'localhost'
      ? '&useApplePaySandbox=true'
      : '';
  const finalUrl = `${paymentUrl}${baseParams}${sandboxParam}`;

  const messageCallbacks: MessageCallbacks = {
    setIsProcessingPayment,
    setTransactionStatus,
    onAlert,
    timeoutRef,
    onAnalyticsEvent: (eventName, params) => {
      sendEvent(eventName, { ...params, source: analyticsSource });
    },
    onCoinbaseApplePayInteraction: fireInteractionOnce,
    onPaymentCancelled,
  };

  // Web: iframe + postMessage
  useEffect(() => {
    if (!paymentUrl || !isWeb) return;

    setIsProcessingPayment?.(true);

    const handleMessage = (ev: MessageEvent) => {
      try {
        const data =
          typeof ev.data === 'string' && ev.data !== 'recaptcha-setup'
            ? JSON.parse(ev.data)
            : ev.data;
        if (data?.eventName) {
          handlePostMessage(data, messageCallbacks);
        }
      } catch {
        // Ignore non-JSON or invalid messages
      }
    };

    window.addEventListener('message', handleMessage);

    timeoutRef.current = setTimeout(() => {
      onAlert?.(
        'Payment Timeout',
        'The payment process took too long. Please try again.',
        'error',
      );
      setIsProcessingPayment?.(false);
    }, 30000);

    return () => {
      window.removeEventListener('message', handleMessage);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setIsProcessingPayment?.(false);
    };
  }, [paymentUrl, isWeb]);

  // Native: hidden webview
  useEffect(() => {
    if (!paymentUrl || isWeb) return;

    setIsProcessingPayment?.(true);

    (async () => {
      await HiddenWebview.create({
        url: finalUrl,
        injectScript: `
          (function(){
            window.addEventListener('message', e => {
              if(window.webkit?.messageHandlers?.hiddenBridge){
                window.webkit.messageHandlers.hiddenBridge.postMessage(e.data);
              }
              if(window.AndroidHiddenBridge){
                AndroidHiddenBridge.postMessage(JSON.stringify(e.data));
              }
            });
          })();
        `,
      });

      timeoutRef.current = setTimeout(() => {
        onAlert?.(
          'Payment Timeout',
          'The payment process took too long. Please try again.',
          'error',
        );
        setIsProcessingPayment?.(false);
      }, 30000);
    })();

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setIsProcessingPayment?.(false);
      HiddenWebview.destroy();
    };
  }, [paymentUrl, isWeb]);

  // Native: listen for webview messages
  useEffect(() => {
    if (isWeb) return;

    let listener: PluginListenerHandle | null = null;
    const setupListener = async () => {
      listener = await HiddenWebview.addListener(
        'webviewMessage',
        (ev: { data: unknown }) => {
          try {
            const data =
              typeof ev.data === 'string' && ev.data !== 'recaptcha-setup'
                ? JSON.parse(ev.data)
                : ev.data;
            const { eventName } = data;

            if (eventName === 'onramp_api.load_success') {
              setIsProcessingPayment?.(true);
              HiddenWebview.evaluateJavascript({
                js: `
                const style=document.createElement('style');
                style.textContent=\`apple-pay-button{display:none!important;}\`;
                document.head.appendChild(style);
                const btn=document.getElementById('api-onramp-apple-pay-button');
                if(btn) btn.click();
              `,
              });
              return;
            }

            handlePostMessage(data, messageCallbacks);
          } catch (err) {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
          }
        },
      );
    };
    setupListener();

    return () => {
      if (listener) {
        listener.remove();
      }
    };
  }, [isWeb]);

  // Web: render visible iframe (user must tap Apple Pay button - no programmatic click)
  if (isWeb && paymentUrl) {
    return (
      <IframeWrapper>
        <StyledIframe
          src={finalUrl}
          title='Apple Pay'
          sandbox='allow-scripts allow-same-origin'
          referrerPolicy='no-referrer'
          allow='payment'
          onLoad={onLoad}
        />
      </IframeWrapper>
    );
  }

  return null;
}
